/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { Box, CircularProgress, Tooltip, Typography } from '@mui/material'
import React from 'react'

type ProductImageBoxProps = {
  imageUrl?: string | null
  size: number
  noImageLabel: string
  alt?: string
  padding?: number
  previewImageSize?: number | null
}

const ProductImageBox: React.FC<ProductImageBoxProps> = ({
  imageUrl,
  size,
  noImageLabel,
  alt = '',
  padding = 0.5,
  previewImageSize = null,
}) => {
  const [loaded, setLoaded] = React.useState(false)
  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => {
    setLoaded(false)
    setFailed(false)
  }, [imageUrl])

  const content = (
    <Box
      sx={theme => ({
        width: size,
        height: size,
        borderRadius: 1.5,
        bgcolor: theme.palette.background.default,
        border: '1px solid',
        borderColor: theme.palette.divider,
        p: padding,
        display: 'grid',
        placeItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
      })}
    >
      {!imageUrl || failed ? (
        <Box
          sx={theme => ({
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            textAlign: 'center',
            px: 0.5,
            color: theme.palette.text.secondary,
          })}
        >
          <Typography
            variant="caption"
            sx={{ fontSize: Math.max(10, Math.round(size * 0.18)) }}
          >
            {noImageLabel}
          </Typography>
        </Box>
      ) : null}
      {imageUrl ? (
        <>
          <Box
            component="img"
            src={imageUrl}
            alt={alt}
            onLoad={() => setLoaded(true)}
            onError={() => {
              setFailed(true)
              setLoaded(false)
            }}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              opacity: loaded && !failed ? 1 : 0,
              transition: 'opacity 140ms ease',
            }}
          />
          {!loaded && !failed ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <CircularProgress size={Math.max(16, Math.round(size * 0.4))} />
            </Box>
          ) : null}
        </>
      ) : null}
    </Box>
  )

  if (!previewImageSize || !imageUrl || failed) {
    return content
  }

  return (
    <Tooltip
      disableHoverListener={!loaded}
      title={
        <Box
          component="img"
          src={imageUrl}
          alt=""
          sx={{
            width: previewImageSize,
            height: previewImageSize,
            objectFit: 'contain',
            display: 'block',
          }}
        />
      }
      placement="bottom-start"
      slotProps={{
        tooltip: {
          sx: theme => ({
            bgcolor: theme.palette.background.paper,
            border: '1px solid',
            borderColor: theme.palette.divider,
            p: 1,
            boxShadow: theme.shadows[8],
          }),
        },
      }}
    >
      <Box sx={{ display: 'inline-flex' }}>{content}</Box>
    </Tooltip>
  )
}

export default ProductImageBox
